import { Button, CloseButton, Group, NativeSelect, Stack, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import {
  getListInvitationsQueryKey,
  useCreateInvitation,
} from "../../api/generated/endpoints/teams/teams";
import type { TeamRole } from "../../api/generated/model";
import { InvitationCreate } from "../../api/generated/zod";
import { ErrorAlert } from "../ErrorAlert";
import { ROLE_LABEL, invitableRoles } from "../permissions";

export interface InviteFormProps {
  teamId: string;
  myRole: TeamRole;
  /** The invitation and its email went: the popover closes, and the page says so. */
  onSent: () => void;
  onCancel: () => void;
}

/**
 * Live's "Add member" (capture design-ref gaps/39; spec teams/invite-by-email): an email, a role
 * this user may give, and Send Invite. The email carries the link, so nothing else shows.
 */
export function InviteForm({ teamId, myRole, onSent, onCancel }: InviteFormProps) {
  const queryClient = useQueryClient();
  const roles = invitableRoles(myRole);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("editor");
  const [invalid, setInvalid] = useState(false);
  // The invitation is saved whether or not its email went: `EMAIL_UNAVAILABLE` comes after the
  // commit (spec platform/email-resend, decision 12). Listing it shows its row and its Resend.
  const saved = async () => {
    setEmail("");
    await queryClient.invalidateQueries({ queryKey: getListInvitationsQueryKey(teamId) });
  };
  const invite = useCreateInvitation({
    mutation: {
      onSuccess: async () => {
        await saved();
        onSent();
      },
      onError: async (error) => {
        if (isApiError(error, "EMAIL_UNAVAILABLE")) await saved();
      },
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = InvitationCreate.safeParse({ email: email.trim(), role });
    setInvalid(!parsed.success);
    if (parsed.success) invite.mutate({ teamId, data: parsed.data });
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700}>Add member</Text>
          <CloseButton aria-label="Close" onClick={onCancel} />
        </Group>
        <TextInput
          label="Email"
          withAsterisk
          type="email"
          autoComplete="off"
          // Live's placeholder, word for word.
          placeholder="name@ejam.com"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          error={invalid ? "Enter an email address, such as name@example.com." : undefined}
        />
        <NativeSelect
          label="Role"
          withAsterisk
          data={roles.map((each) => ({ value: each, label: ROLE_LABEL[each] }))}
          value={role}
          onChange={(event) => {
            const next = roles.find((each) => each === event.currentTarget.value);
            if (next !== undefined) setRole(next);
          }}
        />
        {role === "owner" ? (
          <Text size="sm" c="var(--cai-color-text-muted)">
            An owner invitation hands the team over: when it is accepted, they own the team and you
            become an admin.
          </Text>
        ) : null}
        {invite.isError ? <ErrorAlert error={invite.error} /> : null}
        <Group gap="sm">
          <Button type="submit" loading={invite.isPending}>
            Send Invite
          </Button>
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
